import * as React from "react";
import { X, RotateCcw } from "react-feather";
import { PickerTooltip } from "./PickerTooltip";
import type { DateInputProps } from "../types";
import "./DateInput.css";

// =============================================================================
// DateInput Component
// Free-text input for date/time entry with error state, undo, and clear
// =============================================================================

export const DateInput = React.memo(function DateInput({
  value,
  onChange,
  isError = false,
  previousValue,
  onUndo,
  allowClear = false,
  onClear,
  disabled = false,
  placeholder,
  onFocus,
  onBlur,
  onClick,
  onMouseEnter,
  className,
  style,
  title,
  "aria-label": ariaLabel,
}: DateInputProps) {
  // Internal state to track current input value (may differ from prop during editing)
  const [internalValue, setInternalValue] = React.useState(value);
  const [shouldSyncAfterBlur, setShouldSyncAfterBlur] = React.useState(false);
  const inputRef = React.useRef<HTMLInputElement>(null);
  // Use ref to avoid stale closure issues in blur handler
  const internalValueRef = React.useRef(value);

  // Sync internal value with prop when prop changes
  React.useEffect(() => {
    setInternalValue(value);
    internalValueRef.current = value;
  }, [value]);

  // Force sync after blur - handles case where value prop doesn't change
  // but we still need to reset internal state to match prop
  React.useEffect(() => {
    if (shouldSyncAfterBlur) {
      setInternalValue(value);
      internalValueRef.current = value;
      setShouldSyncAfterBlur(false);
    }
  }, [shouldSyncAfterBlur, value]);

  // ---------------------------------------------------------------------------
  // Event Handlers
  // ---------------------------------------------------------------------------

  const handleChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (disabled) return;
      const newValue = e.target.value;
      setInternalValue(newValue);
      internalValueRef.current = newValue;
    },
    [disabled]
  );

  const handleBlur = React.useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      // Use ref to get latest value and avoid stale closure issues
      const currentValue = internalValueRef.current;
      // Only call onChange if value actually changed
      if (currentValue !== value) {
        onChange(currentValue);
        // Schedule a sync after parent processes onChange
        // This handles cases where the parent transforms the value
        // (e.g., range expression parsing) and the result might be
        // the same as the current value prop
        setShouldSyncAfterBlur(true);
      }
      onBlur?.();
    },
    [value, onChange, onBlur]
  );

  const handleFocus = React.useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      onFocus?.();
    },
    [onFocus]
  );

  const handleClick = React.useCallback(
    (e: React.MouseEvent<HTMLInputElement>) => {
      onClick?.();
    },
    [onClick]
  );

  const handleKeyDown = React.useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (disabled) return;

      if (e.key === "Escape") {
        e.preventDefault();
        setInternalValue("");
        internalValueRef.current = "";
        onChange("");
      } else if (e.key === "Enter") {
        e.preventDefault();
        const currentValue = internalValueRef.current;
        if (currentValue !== value) {
          onChange(currentValue);
        }
        inputRef.current?.blur();
      }
    },
    [disabled, value, onChange]
  );

  const handleClear = React.useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      setInternalValue("");
      onClear?.();
    },
    [onClear]
  );

  const handleUndo = React.useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      onUndo?.();
    },
    [onUndo]
  );

  // ---------------------------------------------------------------------------
  // Computed Values
  // ---------------------------------------------------------------------------

  const showClearButton = allowClear && internalValue && !disabled;
  // previousValue can be "" (empty string) to indicate undo to empty state
  // Only show undo if previous value is different from current value (undo would change something)
  const showUndoButton = isError && previousValue !== null && previousValue !== internalValue && onUndo && !disabled;

  const containerClasses = [
    "date-input",
    isError && "date-input--error",
    disabled && "date-input--disabled",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div className={containerClasses} style={style} onMouseEnter={onMouseEnter}>
      <input
        ref={inputRef}
        type="text"
        className="date-input__field"
        value={internalValue}
        onChange={handleChange}
        onBlur={handleBlur}
        onFocus={handleFocus}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        disabled={disabled}
        aria-label={ariaLabel}
        aria-invalid={isError}
      />

      <PickerTooltip content={title} className="date-input__tooltip" />

      {allowClear && (
        <div className="date-input__actions">
          <button
            type="button"
            className={`date-input__button date-input__button--undo ${showUndoButton ? "date-input__button--visible" : ""}`}
            onClick={handleUndo}
            aria-label="Undo"
            aria-hidden={!showUndoButton}
            tabIndex={-1}
          >
            <RotateCcw size={14} />
          </button>

          <button
            type="button"
            className={`date-input__button date-input__button--clear ${showClearButton ? "date-input__button--visible" : ""}`}
            onClick={handleClear}
            aria-label="Clear"
            aria-hidden={!showClearButton}
            tabIndex={-1}
          >
            <X size={14} />
          </button>
        </div>
      )}
    </div>
  );
});
